import { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { toast } from "sonner";
import { uploadImage } from "@/lib/api";
import {
  Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Link2, Image as ImageIcon, Undo, Redo,
} from "lucide-react";

function Btn({ onClick, active, disabled, title, testid, children }) {
  return (
    <button
      type="button"
      title={title}
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className={`w-8 h-8 flex items-center justify-center rounded-sm transition-colors ${
        active ? "bg-gold text-ink" : "text-muted2 hover:text-bone hover:bg-white/10"
      } disabled:opacity-30`}
    >
      {children}
    </button>
  );
}

export function RichTextEditor({ value, onChange }) {
  const fileRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false }),
      Image.configure({ HTMLAttributes: { class: "rounded-sm" } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: "text-gold underline" } }),
      Placeholder.configure({ placeholder: "Write the article… use the toolbar for formatting and images." }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "tiptap-content min-h-[260px] max-h-[420px] overflow-y-auto px-4 py-3 focus:outline-none text-bone",
        "data-testid": "article-form-content",
      },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const pickImage = () => fileRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const id = toast.loading("Uploading image…");
    try {
      const url = await uploadImage(file);
      editor.chain().focus().setImage({ src: url }).run();
      toast.success("Image inserted.", { id });
    } catch (err) {
      toast.error("Image upload failed.", { id });
    }
  };
  const addLink = () => {
    const url = window.prompt("Link URL");
    if (url) editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    else editor.chain().focus().unsetLink().run();
  };

  return (
    <div className="border border-white/15 rounded-sm bg-elevated" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-1 border-b border-white/15 px-2 py-2">
        <Btn testid="rte-bold" title="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={15} /></Btn>
        <Btn testid="rte-italic" title="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={15} /></Btn>
        <span className="w-px h-5 bg-white/15 mx-1" />
        <Btn testid="rte-h2" title="Heading" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={15} /></Btn>
        <Btn testid="rte-h3" title="Subheading" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 size={15} /></Btn>
        <span className="w-px h-5 bg-white/15 mx-1" />
        <Btn testid="rte-bullet" title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={15} /></Btn>
        <Btn testid="rte-ordered" title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={15} /></Btn>
        <Btn testid="rte-quote" title="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={15} /></Btn>
        <span className="w-px h-5 bg-white/15 mx-1" />
        <Btn testid="rte-link" title="Link" active={editor.isActive("link")} onClick={addLink}><Link2 size={15} /></Btn>
        <Btn testid="rte-image" title="Insert image" onClick={pickImage}><ImageIcon size={15} /></Btn>
        <span className="w-px h-5 bg-white/15 mx-1" />
        <Btn testid="rte-undo" title="Undo" onClick={() => editor.chain().focus().undo().run()}><Undo size={15} /></Btn>
        <Btn testid="rte-redo" title="Redo" onClick={() => editor.chain().focus().redo().run()}><Redo size={15} /></Btn>
      </div>
      <EditorContent editor={editor} />
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="rte-image-input" />
    </div>
  );
}
